import RequireSession from "@/components/RequireSession";
import { AGENT_STATEMENTS_ENABLED } from "@/lib/commissionAccess";

const GODADDY_ALLOWED_PATHS = ["/poynt-collect", "/helpdesk"];

/**
 * Route access policy. Each entry restricts a path prefix to INTERNAL plus the
 * listed extra user types. Anything not listed is open to all authenticated
 * users. Non-internal users (including an INTERNAL admin while IMPERSONATING an
 * agent) who hit a restricted route get the PermissionDenied screen — the
 * backstop for the sidebar hiding these items.
 */
const RESTRICTED_ROUTES: { prefix: string; allow: string[] }[] = [
  { prefix: "/admin", allow: [] }, // INTERNAL only — incl. /admin/users (no agent sub-agent mgmt)
  { prefix: "/ai-call-log", allow: [] },
  { prefix: "/ai-call-log", allow: [] },
  { prefix: "/internal-tools", allow: [] },
  { prefix: "/mailroom", allow: [] }, // INTERNAL only — email templates, sending, logs
  { prefix: "/merchant", allow: [] }, // the Merchant portal preview, on sample data
  // Agents reach only their own statements (/commissions/:reportId), and only once those ship.
  { prefix: "/commissions", allow: AGENT_STATEMENTS_ENABLED ? ["AGENT"] : [] },
  { prefix: "/residuals", allow: [] },
  { prefix: "/reporting", allow: [] },
  { prefix: "/poynt-collect", allow: ["GODADDY_MERCHANT"] },
];

function isRouteDenied(pathname: string, userType: string | undefined): boolean {
  if (!userType || userType === "INTERNAL") return false;
  for (const r of RESTRICTED_ROUTES) {
    const matches = pathname === r.prefix || pathname.startsWith(`${r.prefix}/`);
    if (matches && !r.allow.includes(userType)) return true;
  }
  return false;
}

// GoDaddy merchants only see /poynt-collect and /helpdesk.
function redirectFor(userType: string | undefined, pathname: string): string | null {
  if (userType !== "GODADDY_MERCHANT") return null;
  const allowed = GODADDY_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  return allowed ? null : "/poynt-collect";
}

// GoDaddy is redirected (above); agents/sub-agents see an explicit denial.
const deniedFor = (userType: string | undefined, pathname: string) =>
  userType !== "GODADDY_MERCHANT" && isRouteDenied(pathname, userType);

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <RequireSession redirectFor={redirectFor} deniedFor={deniedFor}>
      {children}
    </RequireSession>
  );
}
